// Mapa para dibujar y editar los polígonos de una zona (Sistema → Zonas).
//
// De fondo: las últimas entregas con coordenadas (para ver dónde cae la
// gente) y los polígonos de las otras zonas, en gris (abajo del mapa se lista
// a qué estantería y a qué tarifa va cada una). Los de esta zona van en color y se editan:
//
//   · «Dibujar polígono»: cada clic agrega un vértice; se cierra tocando el
//     primero o con «Terminar».
//   · Tocar un polígono lo selecciona: los vértices se arrastran, los puntos
//     chicos del medio de cada lado agregan un vértice y el clic derecho
//     saca uno.
//   · El imán pega el vértice a un vértice o a un lado cercano de cualquier
//     polígono, para que los vecinos compartan el borde sin pisarse.
//
// No guarda nada: avisa cada cambio con onChange y guarda el modal.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "../ui/Button";
import { useLeaflet } from "../../hooks/useLeaflet";
import {
  LatLng, Ring, SnapVertex, MIN_VERTICES,
  snapTo, ringSegments, moveVertex, insertMidpoint, removeVertex, midpoints, boundsOf,
} from "./polygonEditing";

export type OtherPolygon = { zone_id: number; name: string; ring: Ring; shelves: { code: string; warehouse: string }[]; tariffs?: { name: string | null; tier: string }[] };

// «Centro → CENTRO (Dropoff) · tarifa Tiendas (GBA1)», o «sin vincular».
const linkedTo = (o: OtherPolygon) => {
  const parts = [
    ...o.shelves.map((s) => `${s.code} (${s.warehouse})`),
    ...(o.tariffs ?? []).map((t) => `tarifa ${t.name ?? "sin nombre"} (${t.tier})`),
  ];
  return parts.length ? parts.join(", ") : "sin vincular";
};
export type MapPoint = { lat: number; lng: number };

const CENTRO: LatLng = [-34.6037, -58.3816];
const OWN = "#2f7cf6";
const OWN_SELECTED = "#f0883e";
const OTHER = "#8b949e";

const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const vertexIcon = (L: any, color: string, size: number) =>
  L.divIcon({ className: "", iconSize: [size, size], html: `<div style="width:${size}px;height:${size}px;border-radius:3px;background:#fff;border:2px solid ${color};box-sizing:border-box"></div>` });

export default function ZonePolygonEditor({
  polygons, onChange, others, points, onDrawingChange,
}: {
  polygons: Ring[];
  onChange: (next: Ring[]) => void;
  // Avisa si hay un polígono a medio dibujar (todavía no está en `polygons`):
  // el modal no deja guardar ni cerrar sin preguntar mientras tanto.
  onDrawingChange?: (drawing: boolean) => void;
  others: OtherPolygon[];
  points: MapPoint[];
}) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const backgroundRef = useRef<any>(null);
  const editRef = useRef<any>(null);
  const fittedRef = useRef(false);

  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<Ring | null>(null);
  useEffect(() => { onDrawingChange?.(draft !== null); }, [draft, onDrawingChange]);
  const [magnet, setMagnet] = useState(true);

  // Lo que el manejador de clics del mapa necesita leer al momento.
  const live = useRef({ polygons, others, draft, selected, magnet });
  live.current = { polygons, others, draft, selected, magnet };

  // Imán: vértices y lados de todo lo dibujado, salvo el polígono que se edita.
  const snap = useCallback((latlng: LatLng, skipOwn: number | null): LatLng => {
    const map = mapRef.current;
    const { polygons: own, others: rest, draft: d, magnet: on } = live.current;
    if (!map || !on) return latlng;
    const L = (window as any).L;
    const project = (r: Ring): SnapVertex[] => r.map(([lat, lng]) => ({ ...map.latLngToContainerPoint(L.latLng(lat, lng)), lat, lng }));
    const rings = [...rest.map((o) => o.ring), ...own.filter((_, i) => i !== skipOwn)].map(project);
    const vertices = [...rings.flat(), ...(d ? project(d) : [])];
    const segments = rings.flatMap(ringSegments);
    const p = map.latLngToContainerPoint(L.latLng(latlng[0], latlng[1]));
    return snapTo(p, vertices, segments) ?? latlng;
  }, []);

  // El mapa se crea una sola vez; el contenedor está siempre en el DOM.
  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, { zoomControl: true, doubleClickZoom: false }).setView(CENTRO, 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
    backgroundRef.current = L.layerGroup().addTo(map);
    editRef.current = L.layerGroup().addTo(map);
    map.on("click", (e: any) => {
      const { draft: d } = live.current;
      if (!d) { setSelected(null); return; }
      setDraft([...d, snap([e.latlng.lat, e.latlng.lng], null)]);
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [leaflet, snap]);

  // Fondo: entregas y otras zonas.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const L = (window as any).L;
    const group = backgroundRef.current;
    group.clearLayers();
    const renderer = L.canvas({ padding: 0.3 });
    for (const p of points) {
      L.circleMarker([p.lat, p.lng], { renderer, radius: 2, stroke: false, fillColor: OTHER, fillOpacity: 0.6, interactive: false }).addTo(group);
    }
    // Sin interacción: así se puede dibujar encima y pegar al borde.
    for (const o of others) {
      L.polygon(o.ring, { color: OTHER, weight: 1.5, dashArray: "4 4", fillOpacity: 0.08, interactive: false }).addTo(group);
    }
  }, [leaflet, points, others]);

  // Encuadre inicial: esta zona, si no las otras, si no las entregas.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current) return;
    const b = boundsOf(polygons) ?? boundsOf(others.map((o) => o.ring)) ?? boundsOf([], points);
    if (!b) return;
    map.fitBounds(b, { padding: [24, 24], maxZoom: 15 });
    fittedRef.current = true;
  }, [leaflet, polygons, others, points]);

  // Lo editable: los polígonos de la zona, el seleccionado con sus vértices,
  // y el borrador que se está dibujando.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const L = (window as any).L;
    const group = editRef.current;
    group.clearLayers();

    polygons.forEach((ring, i) => {
      const isSel = i === selected;
      const shape = L.polygon(ring, { color: isSel ? OWN_SELECTED : OWN, weight: isSel ? 3 : 2, fillOpacity: 0.2, interactive: !draft, bubblingMouseEvents: false });
      shape.on("click", () => setSelected(i));
      shape.addTo(group);
      if (!isSel || draft) return;

      ring.forEach((v, j) => {
        const m = L.marker(v, { icon: vertexIcon(L, OWN_SELECTED, 14), draggable: true, keyboard: false, title: "Arrastrá para mover · clic derecho para sacar" });
        m.on("drag", (e: any) => {
          const ll = e.target.getLatLng();
          shape.setLatLngs(moveVertex(ring, j, [ll.lat, ll.lng]));
        });
        m.on("dragend", (e: any) => {
          const ll = e.target.getLatLng();
          const next = [...polygons];
          next[i] = moveVertex(ring, j, snap([ll.lat, ll.lng], i));
          onChange(next);
        });
        m.on("contextmenu", () => {
          if (ring.length <= MIN_VERTICES) return;
          const next = [...polygons];
          next[i] = removeVertex(ring, j);
          onChange(next);
        });
        m.addTo(group);
      });
      midpoints(ring).forEach((mid, j) => {
        const m = L.marker(mid, { icon: vertexIcon(L, OWN, 10), opacity: 0.8, keyboard: false, title: "Agregar un vértice acá" });
        m.on("click", () => {
          const next = [...polygons];
          next[i] = insertMidpoint(ring, j);
          onChange(next);
        });
        m.addTo(group);
      });
    });

    if (draft) {
      if (draft.length > 1) L.polyline(draft, { color: OWN_SELECTED, weight: 2, dashArray: "6 4", interactive: false }).addTo(group);
      draft.forEach((v, j) => {
        const m = L.marker(v, { icon: vertexIcon(L, OWN_SELECTED, j === 0 ? 16 : 12), keyboard: false, title: j === 0 ? "Tocá acá para cerrar el polígono" : "" });
        if (j === 0) m.on("click", () => finishDraft());
        m.addTo(group);
      });
    }
  });

  const finishDraft = () => {
    const d = live.current.draft;
    if (!d || d.length < MIN_VERTICES) return;
    onChange([...live.current.polygons, d]);
    setSelected(live.current.polygons.length);
    setDraft(null);
  };

  // Una línea por zona (una zona puede tener varios polígonos).
  const otherZones = others.filter((o, i) => others.findIndex((x) => x.zone_id === o.zone_id) === i);

  const removeSelected = () => {
    if (selected === null) return;
    onChange(polygons.filter((_, i) => i !== selected));
    setSelected(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
        {draft ? (
          <>
            <Button onClick={finishDraft} disabled={draft.length < MIN_VERTICES}>Terminar ({draft.length} puntos)</Button>
            <Button variant="secondary" onClick={() => setDraft(draft.slice(0, -1))} disabled={!draft.length}>Deshacer punto</Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>Cancelar</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => { setSelected(null); setDraft([]); }} disabled={!leaflet}>Dibujar polígono</Button>
            {selected !== null ? <Button variant="ghost" onClick={removeSelected}>Borrar el polígono seleccionado</Button> : null}
          </>
        )}
        <label style={{ ...smallMutedStyle, display: "inline-flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
          <input type="checkbox" checked={magnet} onChange={(e) => setMagnet(e.target.checked)} /> Imán (pegar a bordes vecinos)
        </label>
      </div>
      <div style={{ position: "relative" }}>
        <div ref={containerRef} style={{ height: "440px", borderRadius: "14px", overflow: "hidden", border: "1px solid var(--border)" }} />
        {!leaflet ? <div style={{ ...smallMutedStyle, position: "absolute", top: 16, left: 16 }}>Cargando el mapa…</div> : null}
      </div>
      <div style={smallMutedStyle}>
        {draft
          ? "Hacé clic en el mapa para poner cada vértice. Para cerrar, tocá el primero (el más grande) o «Terminar»."
          : `${polygons.length === 1 ? "1 polígono" : `${polygons.length} polígonos`} en esta zona. Tocá uno para editarlo: arrastrá los vértices, el punto del medio de un lado agrega uno y el clic derecho saca uno.`}
        {" "}En gris: otras zonas y las últimas {points.length} entregas con ubicación.
      </div>
      {otherZones.length ? (
        <div style={smallMutedStyle}>
          Otras zonas dibujadas:{" "}
          {otherZones.map((o) => `${o.name} → ${linkedTo(o)}`).join(" · ")}
        </div>
      ) : null}
    </div>
  );
}
